import { Box, Heading, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { EspdCriteriaCategoryDto } from "../../../../../autogen/bff-api";
import { EspdCriterionCard } from "./EspdCriterionCard";

interface Props {
  categories: EspdCriteriaCategoryDto[];
  selectedCriterionIds: string[];
  onToggleCriterion: (id: string, selected: boolean) => void;
}

const EXCLUSION_PREFIXES = ["CRITERION.EXCLUSION"];
const SELECTION_PREFIXES = ["CRITERION.SELECTION"];

const useCategoryLabel = () => {
  const { t } = useTranslation();
  return (category: string): string => {
    const labels: Record<string, string> = {
      EXCLUSION_CONVICTIONS: t("Convictions"),
      EXCLUSION_CONTRIBUTIONS: t("Payment of taxes and social security"),
      EXCLUSION_SOCIAL: t("Social, environmental, and labour law"),
      EXCLUSION_BUSINESS: t("Business situation"),
      EXCLUSION_MISCONDUCT: t("Professional misconduct"),
      EXCLUSION_CONFLICT: t("Conflict of interest"),
      EXCLUSION_NATIONAL: t("National exclusion grounds"),
      SELECTION_SUITABILITY: t("Suitability"),
      SELECTION_ECONOMIC: t("Economic and financial standing"),
      SELECTION_TECHNICAL: t("Technical and professional ability"),
      SELECTION_QUALITY: t("Quality assurance"),
      OTHER_EO_DATA: t("Economic operator data"),
    };
    return labels[category] ?? category;
  };
};

export const EspdCriterionSelector = ({ categories, selectedCriterionIds, onToggleCriterion }: Props) => {
  const { t } = useTranslation();
  const getCategoryLabel = useCategoryLabel();

  const exclusionCategories = categories.filter((c) =>
    EXCLUSION_PREFIXES.some((prefix) => c.typeCodePrefix.startsWith(prefix))
  );
  const selectionCategories = categories.filter((c) =>
    SELECTION_PREFIXES.some((prefix) => c.typeCodePrefix.startsWith(prefix))
  );
  const otherCategories = categories.filter(
    (c) =>
      !EXCLUSION_PREFIXES.some((prefix) => c.typeCodePrefix.startsWith(prefix)) &&
      !SELECTION_PREFIXES.some((prefix) => c.typeCodePrefix.startsWith(prefix))
  );

  const renderSection = (title: string, sectionCategories: EspdCriteriaCategoryDto[]) => {
    if (sectionCategories.length === 0) return null;
    return (
      <Box mb="8">
        <Heading as="h3" size="md" mb="4" color="teal.700" borderBottom="2px solid" borderColor="teal.500" pb="2">
          {title}
        </Heading>
        {sectionCategories.map((cat) => (
          <Box key={cat.category} mb="4" bg="gray.50" p="4" borderRadius="md">
            <Text fontWeight="bold" fontSize="md" mb="3" color="gray.700" textTransform="uppercase" letterSpacing="wide">
              {getCategoryLabel(cat.category)}
            </Text>
            {cat.criteria.map((criterion) => (
              <EspdCriterionCard
                key={criterion.id}
                criterion={criterion}
                isSelected={selectedCriterionIds.includes(criterion.id)}
                onToggle={onToggleCriterion}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {renderSection(t("Part III — Exclusion Grounds"), exclusionCategories)}
      {renderSection(t("Part IV — Selection Criteria"), selectionCategories)}
      {renderSection(t("Other"), otherCategories)}
    </Box>
  );
};
